import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  approvePathologyResult,
  getPathologyResultById,
  listPathologyLabs,
  listPathologyResults,
  mapDetailToUi,
  mapUiToSavePayload,
  savePathologyResult,
} from "../../services/pathologyResultService";
import {
  DOCTOR_OPTIONS,
  PATHOLOGIST_OPTIONS,
  getDefaultFilters,
} from "../../pages/PathologyResultApproval/pathologyResultApprovalData";

const initialState = {
  filters: getDefaultFilters(),
  tests: [],
  labs: [],
  selectedId: null,
  selectedDetail: null,
  loading: false,
  detailLoading: false,
  saving: false,
  approving: false,
  labsLoading: false,
  error: null,
};

export const fetchPathologyResults = createAsyncThunk(
  "pathologyResult/fetchPathologyResults",
  async (filters, { rejectWithValue }) => {
    try {
      return await listPathologyResults(filters || getDefaultFilters());
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPathologyResultDetail = createAsyncThunk(
  "pathologyResult/fetchPathologyResultDetail",
  async (pathReportId, { rejectWithValue }) => {
    try {
      const detail = await getPathologyResultById(pathReportId);
      return mapDetailToUi(detail, PATHOLOGIST_OPTIONS, DOCTOR_OPTIONS);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const savePathologyResultEntry = createAsyncThunk(
  "pathologyResult/savePathologyResultEntry",
  async ({ record, userId }, { rejectWithValue }) => {
    try {
      if (!record?.pathReportId && !record?.id) {
        return rejectWithValue("Missing pathology report id");
      }
      const pathReportId = record.pathReportId || record.id;
      await savePathologyResult(pathReportId, mapUiToSavePayload(record, userId));
      return {
        ...record,
        id: pathReportId,
        pathReportId,
        reportStatus: "Completed",
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const approvePathologyResultEntry = createAsyncThunk(
  "pathologyResult/approvePathologyResultEntry",
  async ({ pathReportId, userId }, { rejectWithValue }) => {
    try {
      await approvePathologyResult(pathReportId, userId);
      return pathReportId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPathologyLabs = createAsyncThunk(
  "pathologyResult/fetchPathologyLabs",
  async (_, { rejectWithValue }) => {
    try {
      return await listPathologyLabs();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const pathologyResultSlice = createSlice({
  name: "pathologyResult",
  initialState,
  reducers: {
    setPathologyFilters: (state, action) => {
      state.filters = action.payload || getDefaultFilters();
    },
    setSelectedPathologyId: (state, action) => {
      state.selectedId = action.payload;
      if (!action.payload) {
        state.selectedDetail = null;
      }
    },
    clearPathologyError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPathologyResults.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPathologyResults.fulfilled, (state, action) => {
        state.loading = false;
        state.tests = action.payload || [];
      })
      .addCase(fetchPathologyResults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load pathology results";
        state.tests = [];
      })
      .addCase(fetchPathologyResultDetail.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchPathologyResultDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedDetail = action.payload;
        state.selectedId = action.payload?.pathReportId ?? state.selectedId;
      })
      .addCase(fetchPathologyResultDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload || "Failed to load pathology result";
        state.selectedDetail = null;
      })
      .addCase(savePathologyResultEntry.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(savePathologyResultEntry.fulfilled, (state, action) => {
        state.saving = false;
        const updated = action.payload;
        state.tests = state.tests.map((t) =>
          t.id === updated.id
            ? {
                ...t,
                ...updated,
                reportStatus: "Completed",
              }
            : t
        );
        state.selectedDetail = updated;
      })
      .addCase(savePathologyResultEntry.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || "Failed to save pathology result";
      })
      .addCase(approvePathologyResultEntry.pending, (state) => {
        state.approving = true;
        state.error = null;
      })
      .addCase(approvePathologyResultEntry.fulfilled, (state, action) => {
        state.approving = false;
        const id = action.payload;
        state.tests = state.tests.map((t) =>
          t.id === id
            ? { ...t, approved: true, reportStatus: "Completed" }
            : t
        );
        if (state.selectedDetail?.id === id) {
          state.selectedDetail = {
            ...state.selectedDetail,
            approved: true,
            reportStatus: "Completed",
          };
        }
      })
      .addCase(approvePathologyResultEntry.rejected, (state, action) => {
        state.approving = false;
        state.error = action.payload || "Failed to approve pathology result";
      })
      .addCase(fetchPathologyLabs.pending, (state) => {
        state.labsLoading = true;
      })
      .addCase(fetchPathologyLabs.fulfilled, (state, action) => {
        state.labsLoading = false;
        state.labs = action.payload || [];
      })
      .addCase(fetchPathologyLabs.rejected, (state) => {
        state.labsLoading = false;
        state.labs = [];
      });
  },
});

export const {
  setPathologyFilters,
  setSelectedPathologyId,
  clearPathologyError,
} = pathologyResultSlice.actions;

export default pathologyResultSlice.reducer;
